import { MagnifyingGlassIcon, XIcon } from '@phosphor-icons/react';
import classNames from 'classnames';
import {
  ChangeEvent,
  forwardRef,
  ReactNode,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { translate } from 'waldur-i18n-runtime';
import { BaseButton } from 'waldur-ui';

import { BaseInputField, BaseInputFieldProps } from './InputField';

interface FilterBoxProps extends BaseInputFieldProps {
  autoFocus?: boolean;
  /**
   * Enter is swallowed so it doesn't submit an enclosing form. Pass false
   * when something above handles Enter itself (a combobox list selecting its
   * highlighted row).
   */
  preventEnterSubmit?: boolean;
  inputClassName?: string;
  rightAction?: ReactNode;
}

/** A search field; the ref is its input's. */
export const FilterBox = forwardRef<HTMLInputElement, FilterBoxProps>(
  (
    {
      className,
      autoFocus,
      inputClassName,
      rightAction,
      preventEnterSubmit = true,
      ...props
    }: any,
    ref,
  ) => {
    const inputRef = useRef<HTMLInputElement>(null);
    useImperativeHandle(ref, () => inputRef.current, []);
    useEffect(() => {
      if (!autoFocus) {
        return;
      }
      if (!inputRef) {
        return;
      }
      inputRef?.current.focus();
    }, [inputRef, autoFocus]);

    // A search box gets the clear button the header search has
    // (SearchInput.tsx), in place of the browser's own blue cross. The box may be controlled or
    // not, so its text is tracked here when no `value` is given.
    const isSearch = props.type === 'search';
    const [uncontrolledValue, setUncontrolledValue] = useState(
      props.defaultValue ?? '',
    );
    const value = props.value !== undefined ? props.value : uncontrolledValue;
    const showClear = isSearch && String(value ?? '') !== '' && !props.disabled;

    const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
      setUncontrolledValue(e.target.value);
      props.onChange?.(e);
    };

    // Clears the box as typing would, so every caller's onChange sees an
    // ordinary change event: React notices a value set past its own setter
    // and reports the input event that follows.
    const clear = () => {
      const input = inputRef.current;
      if (!input) return;
      Object.getOwnPropertyDescriptor(
        HTMLInputElement.prototype,
        'value',
      )?.set?.call(input, '');
      input.dispatchEvent(new Event('input', { bubbles: true }));
      input.focus();
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
      // Prevent form submission when Enter is pressed in search input
      if (preventEnterSubmit && e.key === 'Enter') {
        e.preventDefault();
      }
      // Call original onKeyDown if provided
      if (props.onKeyDown) {
        props.onKeyDown(e);
      }
    };

    return (
      <BaseInputField
        type="text"
        containerClassName={classNames('filter-box', className)}
        start={
          <span className="[&>svg]:size-[20px]">
            <MagnifyingGlassIcon weight="bold" />
          </span>
        }
        end={
          (rightAction || showClear) && (
            <span className="pointer-events-auto flex items-center gap-1.5">
              {showClear && (
                <BaseButton
                  size="sm"
                  variant="text-secondary"
                  onClick={clear}
                  iconNode={<XIcon weight="bold" />}
                  tooltip={translate('Clear')}
                />
              )}
              {rightAction}
            </span>
          )
        }
        className={classNames(
          isSearch && '[&::-webkit-search-cancel-button]:appearance-none',
          inputClassName,
        )}
        {...props}
        ref={inputRef}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
      />
    );
  },
);
FilterBox.displayName = 'FilterBox';
